import type { Language } from '@eformulieren/engine'
import type { ActionOutcome, InvitationOutcome, InviteRequest, ShareLine } from '@eformulieren/renderer'
import { CLIENT_HEADER, WRITE_HEADERS } from './podRequests.js'
import { NO_CONNECTION } from './rejection.js'
import { isSessionExpiredResponse } from './session.js'
import { type ReadAdvice, type ReadOverview, adviceBody, readAdvice, readOverview, rejectionFromBody, shareNotice } from './sharing.js'
import { type ReadAdviceFull, readAdviceFull } from './advice.js'

/**
 * De verzoeken van de dialoog "Delen" aan de pod (R-64, R-65, R-66, R-66a, R-184, `api-contract.md`
 * 1, 3 en 6, ontwerp 6.4, 7.4a en 9.2).
 *
 * Op de eigen oorsprong, met de koppen van de pagina (een token van een proef, of niets en dan het
 * cookie) en de CSRF-header bij elke schrijfactie, zoals bij indienen. Wie deelt en aan welke kant zet
 * de pod uit het token (R-154); de pagina stuurt alleen het adres, de rechten en bij advies de vraag.
 * Geen antwoord draagt een code: de link gaat langs de outbox naar het processysteem (R-64b).
 *
 *   - `POST /delingen` nodigt uit, en nodigt opnieuw uit met dezelfde rechten (besluit 3).
 *   - `PATCH /delingen/:id` wijzigt de rechten van een lopende regel (besluit 3, 9).
 *   - `DELETE /delingen/:id` trekt in, ook een open uitnodiging.
 *   - `POST /advies` vraagt advies; dat maakt zelf de uitnodiging, dus het is één verzoek.
 *   - `POST /advies/:id/herinneren`, `.../intrekken`, `.../reacties`, `.../afronden`, `.../termijn`,
 *     `.../belangoordeel` (het oordeel van NWO over de belangenverklaring, R-184k)
 *     voor het adviespaneel, en `.../belangenverklaring` voor de adviseurpagina.
 *   - `GET /delingen` en `GET /advies` halen de lijst en wat er bij advies te kiezen is.
 *
 * **Fouten.** De ruwe keten naar `log` (de console) en nooit naar het scherm (ontwerp 11.4); naar het
 * scherm een zin met de secties bij naam (`shareNotice`). Een 401 meldt de verlopen sessie aan de
 * pagina. `GET /advies` dat mislukt is "kan niet vragen": de dialoog werkt dan zonder advies.
 */

export interface ShareClientOptions {
  readonly instantieId: string
  /** De koppen voor een verzoek: een token als er een is, anders niets (`headersFor`). */
  readonly headers: Readonly<Record<string, string>>
  readonly taal: Language
  readonly sectionName: (sectionId: string) => string | undefined
  /** `fetch` op de eigen oorsprong; injecteerbaar voor de test. */
  readonly fetch: (path: string, init?: RequestInit) => Promise<Response>
  readonly onSessionExpired: () => void
  readonly log: (chain: Readonly<Record<string, unknown>>) => void
}

export interface ShareClient {
  readonly overview: () => Promise<ReadOverview | 'mislukt'>
  readonly advice: () => Promise<ReadAdvice>
  readonly invite: (request: InviteRequest) => Promise<InvitationOutcome>
  readonly change: (id: string, rights: Readonly<Record<string, 'lezen' | 'bewerken'>>) => Promise<ActionOutcome>
  readonly withdraw: (id: string) => Promise<ActionOutcome>
  readonly inviteAgain: (line: Pick<ShareLine, 'id' | 'email' | 'rechten'>) => Promise<ActionOutcome>
  readonly remind: (adviesId: string) => Promise<ActionOutcome>
  readonly withdrawAdvice: (adviesId: string) => Promise<ActionOutcome>
  /** `GET /advies` met het gesprek erbij (`advice.ts`), of niets bij een storing. */
  readonly adviceFull: () => Promise<ReadAdviceFull | undefined>
  readonly react: (adviesId: string, tekst: string) => Promise<ActionOutcome>
  readonly finish: (adviesId: string) => Promise<ActionOutcome>
  readonly deadline: (adviesId: string, termijn: string | null) => Promise<ActionOutcome>
  /** Het oordeel van NWO over de belangenverklaring, met een verplichte toelichting (R-184k). */
  readonly judge: (adviesId: string, uitkomst: 'geen-belang' | 'belang', toelichting: string) => Promise<ActionOutcome>
  /**
   * De belangenverklaring. Wijst af bij een weigering, want zo toont `InterestDeclaration` haar melding
   * aan de knop; de ruwe keten staat dan in de console.
   */
  readonly declare: (adviesId: string, verklaring: { readonly uitkomst: 'geen-belang' | 'belang'; readonly toelichting: string | null }) => Promise<void>
}

const NO_ADVICE: ReadAdvice = { kanVragen: false, buitenNwo: false, uitgesloten: [], verzoeken: [] }

type Sent = { readonly soort: 'antwoord'; readonly body: unknown } | { readonly soort: 'geweigerd'; readonly melding: ReturnType<typeof shareNotice> }

export function createShareClient(options: ShareClientOptions): ShareClient {
  const { instantieId, headers, taal: language, fetch } = options
  const instantie = encodeURIComponent(instantieId)
  const name = options.sectionName

  async function send(method: 'POST' | 'PATCH' | 'DELETE', path: string, body: unknown, expected: number): Promise<Sent> {
    try {
      const response = await fetch(path, {
        method,
        headers: method === 'DELETE' ? { ...headers, ...CLIENT_HEADER } : { ...headers, ...WRITE_HEADERS },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      })
      const text = await response.text()
      if (response.status === expected) {
        try {
          return { soort: 'antwoord', body: text === '' ? undefined : (JSON.parse(text) as unknown) }
        } catch {
          return { soort: 'antwoord', body: undefined }
        }
      }
      options.log({ methode: method, pad: path, status: response.status, lijf: text.slice(0, 2000) })
      if (isSessionExpiredResponse(response.status)) options.onSessionExpired()
      return { soort: 'geweigerd', melding: shareNotice(rejectionFromBody(text), language, name) }
    } catch (error) {
      options.log({ methode: method, pad: path, fout: error })
      return { soort: 'geweigerd', melding: shareNotice({ code: NO_CONNECTION, secties: [], problemen: [] }, language, name) }
    }
  }

  async function get(path: string): Promise<unknown> {
    try {
      const response = await fetch(path, { headers })
      if (!response.ok) {
        options.log({ methode: 'GET', pad: path, status: response.status, lijf: (await response.text()).slice(0, 2000) })
        if (isSessionExpiredResponse(response.status)) options.onSessionExpired()
        return undefined
      }
      return (await response.json()) as unknown
    } catch (error) {
      options.log({ methode: 'GET', pad: path, fout: error })
      return undefined
    }
  }

  const action = (sent: Sent): ActionOutcome => (sent.soort === 'antwoord' ? { soort: 'gelukt' } : sent)

  /** Het id van de nieuwe regel uit een antwoord: de regel zelf, of bij advies de uitnodiging van het verzoek. */
  function newLine(body: unknown, key: 'id' | 'uitnodigingId'): string | undefined {
    if (typeof body !== 'object' || body === null) return undefined
    const value = (body as Record<string, unknown>)[key]
    return typeof value === 'string' ? value : undefined
  }

  return {
    async overview() {
      const path = `/delingen?instantie=${instantie}`
      const body = await get(path)
      if (body === undefined) return 'mislukt'
      const read = readOverview(body)
      if (read === undefined) options.log({ methode: 'GET', pad: path, fout: 'het overzicht past niet op de vorm' })
      return read ?? 'mislukt'
    },
    async advice() {
      const path = `/advies?instantie=${instantie}`
      const body = await get(path)
      if (body === undefined) return NO_ADVICE
      const read = readAdvice(body)
      if (read === undefined) options.log({ methode: 'GET', pad: path, fout: 'het antwoord over advies past niet op de vorm' })
      return read ?? NO_ADVICE
    },
    async invite(request) {
      if (request.soort === 'advies') {
        const sent = await send('POST', '/advies', adviceBody(instantieId, request), 201)
        if (sent.soort === 'geweigerd') return sent
        const id = newLine(sent.body, 'uitnodigingId')
        return id === undefined ? { soort: 'verstuurd' } : { soort: 'verstuurd', id }
      }
      const sent = await send('POST', '/delingen', { instantie: instantieId, email: request.email, rechten: request.rechten }, 201)
      if (sent.soort === 'geweigerd') return sent
      const id = newLine(sent.body, 'id')
      return id === undefined ? { soort: 'verstuurd' } : { soort: 'verstuurd', id }
    },
    async change(id, rights) {
      return action(await send('PATCH', `/delingen/${encodeURIComponent(id)}?instantie=${instantie}`, { rechten: rights }, 200))
    },
    async withdraw(id) {
      return action(await send('DELETE', `/delingen/${encodeURIComponent(id)}?instantie=${instantie}`, undefined, 200))
    },
    async inviteAgain(line) {
      if (line.email === undefined) return { soort: 'geweigerd', melding: shareNotice({ secties: [], problemen: [] }, language, name) }
      return action(await send('POST', '/delingen', { instantie: instantieId, email: line.email, rechten: line.rechten }, 201))
    },
    async remind(adviesId) {
      return action(await send('POST', `/advies/${encodeURIComponent(adviesId)}/herinneren?instantie=${instantie}`, {}, 200))
    },
    async withdrawAdvice(adviesId) {
      return action(await send('POST', `/advies/${encodeURIComponent(adviesId)}/intrekken?instantie=${instantie}`, {}, 200))
    },
    async adviceFull() {
      const path = `/advies?instantie=${instantie}`
      const body = await get(path)
      if (body === undefined) return undefined
      const read = readAdviceFull(body)
      if (read === undefined) options.log({ methode: 'GET', pad: path, fout: 'het antwoord over advies past niet op de vorm' })
      return read
    },
    async react(adviesId, tekst) {
      return action(await send('POST', `/advies/${encodeURIComponent(adviesId)}/reacties?instantie=${instantie}`, { tekst }, 200))
    },
    async finish(adviesId) {
      return action(await send('POST', `/advies/${encodeURIComponent(adviesId)}/afronden?instantie=${instantie}`, {}, 200))
    },
    async deadline(adviesId, termijn) {
      return action(await send('POST', `/advies/${encodeURIComponent(adviesId)}/termijn?instantie=${instantie}`, { termijn }, 200))
    },
    async judge(adviesId, uitkomst, toelichting) {
      return action(await send('POST', `/advies/${encodeURIComponent(adviesId)}/belangoordeel?instantie=${instantie}`, { uitkomst, toelichting }, 200))
    },
    async declare(adviesId, verklaring) {
      const sent = await send('POST', `/advies/${encodeURIComponent(adviesId)}/belangenverklaring?instantie=${instantie}`, verklaring, 200)
      if (sent.soort === 'geweigerd') throw new Error(sent.melding.tekst)
    },
  }
}
